/**
 * Theme Service - Manages dark/light mode with localStorage persistence
 */

const THEME_KEY = 'memo-fast-theme'
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
}

class ThemeService {
  constructor() {
    this.currentTheme = THEMES.SYSTEM
    this.listeners = []
  }

  /**
   * Initialize theme from localStorage or system preference
   */
  init() {
    const savedTheme = localStorage.getItem(THEME_KEY)

    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      this.currentTheme = savedTheme
    } else {
      this.currentTheme = THEMES.SYSTEM
    }

    this.applyTheme()
    this.setupSystemListener()
  }

  /**
   * Get the current theme setting
   */
  getTheme() {
    return this.currentTheme
  }

  /**
   * Get the actual applied theme (resolves 'system' to actual value)
   */
  getAppliedTheme() {
    if (this.currentTheme === THEMES.SYSTEM) {
      return this.getSystemPreference()
    }
    return this.currentTheme
  }

  /**
   * Set theme and persist to localStorage
   */
  setTheme(theme) {
    if (!Object.values(THEMES).includes(theme)) {
      console.warn(`Invalid theme: ${theme}`)
      return
    }

    this.currentTheme = theme
    localStorage.setItem(THEME_KEY, theme)
    this.applyTheme()
    this.notifyListeners()
  }

  /**
   * Toggle between light and dark (skips system)
   */
  toggle() {
    const applied = this.getAppliedTheme()
    const newTheme = applied === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK
    this.setTheme(newTheme)
  }

  /**
   * Cycle through all themes: light -> dark -> system
   */
  cycle() {
    const order = [THEMES.LIGHT, THEMES.DARK, THEMES.SYSTEM]
    const currentIndex = order.indexOf(this.currentTheme)
    const nextIndex = (currentIndex + 1) % order.length
    this.setTheme(order[nextIndex])
  }

  /**
   * Apply theme to document
   */
  applyTheme() {
    const html = document.documentElement
    const applied = this.getAppliedTheme()

    if (applied === THEMES.DARK) {
      html.setAttribute('data-theme', 'dark')
    } else {
      html.setAttribute('data-theme', 'light')
    }
  }

  /**
   * Get system color scheme preference
   */
  getSystemPreference() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEMES.DARK
    }
    return THEMES.LIGHT
  }

  /**
   * Listen for system preference changes
   */
  setupSystemListener() {
    if (window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.currentTheme === THEMES.SYSTEM) {
          this.applyTheme()
          this.notifyListeners()
        }
      })
    }
  }

  /**
   * Subscribe to theme changes
   */
  subscribe(callback) {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  }

  /**
   * Notify all listeners of theme change
   */
  notifyListeners() {
    const applied = this.getAppliedTheme()
    this.listeners.forEach(callback => callback(applied, this.currentTheme))
  }

  /**
   * Check if dark mode is currently active
   */
  isDark() {
    return this.getAppliedTheme() === THEMES.DARK
  }
}

// Singleton instance
const themeService = new ThemeService()

export { themeService, THEMES }
export default themeService
